import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import AuthLayout from "@/components/AuthLayout";
import { useAuth } from "@/lib/AuthContext";
import { configureIap, lifetimePackage, buyPackage, restorePurchases, syncAccess } from "@/lib/iap";

const FEATURES = ["photos", "reports", "tracking", "treatments"];

export default function Paywall() {
  const { t } = useTranslation();
  const { user, logout, checkUserAuth } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    configureIap(user.id).then(lifetimePackage)
      .then((found) => { setPkg(found); setState(found ? "ready" : "unavailable"); })
      .catch(() => setState("unavailable"));
  }, [user.id]);

  const unlock = async (action) => {
    setState("busy");
    try {
      await action();
      const updated = await syncAccess();
      if (updated.has_access) return checkUserAuth();
      toast.error(t("paywall.notFound"));
    } catch (error) {
      if (!error?.userCancelled && error?.code !== "1") toast.error(t("paywall.error"));
    }
    setState(pkg ? "ready" : "unavailable");
  };

  const busy = state === "busy" || state === "loading";
  return (
    <AuthLayout icon={Sparkles} title={t("paywall.title")} subtitle={t("paywall.subtitle")}>
      <div data-testid="paywall-page" className="space-y-5">
        <ul className="space-y-2 text-sm text-foreground">
          {FEATURES.map((f) => <li key={f} className="flex gap-2"><span className="text-primary">✓</span>{t(`paywall.features.${f}`)}</li>)}
        </ul>
        {state === "unavailable" && <p data-testid="paywall-unavailable" className="text-sm text-muted-foreground text-center">{t("paywall.unavailable")}</p>}
        <Button data-testid="paywall-buy-btn" className="w-full h-12" disabled={busy || !pkg} onClick={() => unlock(() => buyPackage(pkg))}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("paywall.buy", { price: pkg?.product?.priceString || "" })}
        </Button>
        <p className="text-xs text-muted-foreground text-center">{t("paywall.oneTime")}</p>
        <Button data-testid="paywall-restore-btn" variant="outline" className="w-full" disabled={busy} onClick={() => unlock(restorePurchases)}>
          {t("paywall.restore")}
        </Button>
        <div className="flex justify-between text-xs text-muted-foreground">
          <Link to="/terms" data-testid="paywall-terms-link">{t("paywall.terms")}</Link>
          <Link to="/privacy" data-testid="paywall-privacy-link">{t("paywall.privacy")}</Link>
          <button type="button" data-testid="paywall-logout-btn" onClick={() => logout()}>{t("paywall.logout")}</button>
        </div>
      </div>
    </AuthLayout>
  );
}
